import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Box, Card, CardContent, Typography, Button, Chip,
  TableCell, TableRow, IconButton, useMediaQuery,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, InputLabel, Select, MenuItem, Avatar,
  ListItemText, Checkbox, CircularProgress, InputAdornment, Skeleton, Tooltip
} from '@mui/material';
import {
  Add, 
  Search,
  Assignment,
  Group,
  AutoAwesome,
  Warning,
  KeyboardArrowDown,
  KeyboardArrowUp
} from '@mui/icons-material';
import Navbar from '../shared/Navbar';
import CommonTable from '../shared/CommonTable';
import CommonFormModal from '../shared/CommonFormModal';
import {
  fetchProjects,
  fetchDropdownData,
  fetchProjectMembers,
  createProject,
  updateProject,
  deleteProject,
  clearProjectMembers,
  fetchAllExistingPrefixes
} from '../../store/projectSlice';
import { setFilters, clearFilters, fetchProjectDropdownsByUser, saveUserPreference } from '../../store/ticketSlice';
import { fetchAllUsers } from '../../store/userSlice';
import { COLORS } from '../../shared/colors';
import { REGEX } from '../../shared/regex';
import { ICONS } from '../../shared/icons';
import '../users/UserManagement.css';
import '../../shared/globalStyles.scss';
import './ProjectManagement.scss';
import { TOAST_MESSAGES, PROJECT, VALIDATION_MESSAGES, CHARACTER_LIMITS, PROJECT_TYPES, SCRUM_ROLES, USER_ROLES, STORAGE_KEYS, MANAGEMENT_FILTER_STATUS } from '../../shared/appConstants';
import { projectManagementStyles } from '../../shared/globalSX';
import OverflowTooltip from '../../shared/OverflowTooltip';
import TeamMembersDialog from '../shared/TeamMembersDialog';
import { cleanDeletedEmail } from '../../shared/commonFunctions';
import kanbanIcon from '../../assets/images/kanban_icon.svg';
import scrumIcon from '../../assets/images/scrum_icon.svg';

// Constants for prefix validation
const PREFIX_MIN_LENGTH = 2;
const PREFIX_MAX_LENGTH = 5;
const PROJECT_DROPDOWN_STATUSES = ['ACTIVE', 'INACTIVE', 'SOFT-DELETE'];
const PROJECT_LIST_STATUSES = [MANAGEMENT_FILTER_STATUS.ACTIVE, MANAGEMENT_FILTER_STATUS.INACTIVE];

// Local MenuProps and MenuItem styles for standardized dropdowns
const dropdownMenuProps = {
  PaperProps: {
    style: {
      maxHeight: 300,
      width: 350,
      borderRadius: '12px',
      marginTop: '8px',
      boxShadow: `4px 4px 4px 0px ${COLORS.ui.projectCardShadow}`,
      border: '0.6px solid transparent',
      backgroundImage: `linear-gradient(${COLORS.white}, ${COLORS.white}), linear-gradient(179.65deg, ${COLORS.ui.projectCardBorderLight} 0.33%, ${COLORS.ui.projectCardBorderDark} 99.73%)`,
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box'
    }
  }
};

const roleDropdownMenuProps = {
  PaperProps: {
    style: {
      maxHeight: 250,
      width: 'auto',
      backgroundColor: COLORS.white,
      borderRadius: '2px',
      marginTop: '4px',
      boxShadow: '0px 2px 4px rgba(0,0,0,0.1)'
    }
  }
};

const dropdownMenuItemSx = {
  py: 1.5,
  px: 2,
  mx: 1,
  borderRadius: '6px',
  fontSize: '0.875rem',
  gap: 1.5,
  borderBottom: `1.5px solid ${COLORS.gray[200]}`,
  '&:hover': { bgcolor: COLORS.ui.menuHover },
  '&:last-of-type': {
    borderBottom: 'none'
  }
};

const localStyles = {
  actionButtons: { display: 'flex', gap: 1 },
  loadingContainer: { display: 'flex', justify: 'center', align: 'center', height: '300px' },
  userAvatar: { bgcolor: '#082B72', width: 28, height: 28, fontSize: '12px' },
  deleteDialogAvatar: { bgcolor: COLORS.error },
  selectValueWithAvatar: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    minWidth: 0
  },
  selectValueText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  }
};


const getUserInitials = (name) => {
  if (!name) return '';
  const nameParts = name.split(' ');
  return nameParts.length >= 2 
    ? `${nameParts[0]?.[0] || ''}${nameParts[nameParts.length - 1]?.[0] || ''}` 
    : name?.[0] || '';
};

const ProjectManagement = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { projects, spaces, statusOptions, projectMembers, allExistingPrefixes, loading, dropdownsLoading } = useSelector(state => state.projects);
  const { allUsers: users, allUsersLoading } = useSelector(state => state.users);
  const { projectDropdownsByUser } = useSelector(state => state.tickets);
  const { user } = useSelector(state => state.auth);
  
  const canManageProjects = user?.role === USER_ROLES.ADMIN || user?.role === USER_ROLES.PM;
  
  const isSmallScreen = useMediaQuery('(max-width: 1440px)');

  const statusParam = (searchParams.get('status') || '').toUpperCase();
  const targetSpaceId = searchParams.get('spaceId') || '';
  const spaceSectionRefs = useRef({});
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const hasProjectSearch = projectSearchTerm.trim().length > 0;
  const [collapsedSpaces, setCollapsedSpaces] = useState(() => {
    try {
      const savedSpaces = JSON.parse(sessionStorage.getItem(STORAGE_KEYS.PROJECT_COLLAPSED_SPACES) || '[]');
      return Array.isArray(savedSpaces) ? savedSpaces.map(String) : [];
    } catch {
      return [];
    }
  });
  const [selectedStatus, setSelectedStatus] = useState(() => {
    if (statusParam === MANAGEMENT_FILTER_STATUS.ALL) return '';
    if (PROJECT_LIST_STATUSES.includes(statusParam)) return statusParam;
    const savedStatus = sessionStorage.getItem(STORAGE_KEYS.PROJECT_STATUS_FILTER) || '';
    return PROJECT_LIST_STATUSES.includes(savedStatus) ? savedStatus : '';
  });

  const expandAllSpaces = () => {
    setCollapsedSpaces([]);
    sessionStorage.removeItem(STORAGE_KEYS.PROJECT_COLLAPSED_SPACES);
  };

  useEffect(() => {
    if (statusParam === MANAGEMENT_FILTER_STATUS.ALL) {
      setSelectedStatus('');
      return;
    }
    if (PROJECT_LIST_STATUSES.includes(statusParam)) setSelectedStatus(statusParam);
  }, [statusParam]);

  const displayProjects = useMemo(() => {
    const groupedProjects = projects || {};
    const normalizedSearchTerm = projectSearchTerm.trim().toLowerCase();

    return Object.fromEntries(
      Object.entries(groupedProjects)
        .map(([spaceName, spaceProjects]) => [
          spaceName,
          spaceProjects.filter((project) => {
            const matchesStatus = !selectedStatus || project?.status?.toUpperCase() === selectedStatus;
            const matchesSearch = !normalizedSearchTerm || [
              project?.name,
              project?.ticket_number_prefix,
              project?.lead_name
            ].some((value) => String(value || '').toLowerCase().includes(normalizedSearchTerm));

            return matchesStatus && matchesSearch;
          })
        ])
        .filter(([, spaceProjects]) => spaceProjects.length > 0)
    );
  }, [projects, selectedStatus, projectSearchTerm]);

  useEffect(() => {
    if (!targetSpaceId || loading || !Array.isArray(spaces) || spaces.length === 0) return;
    const normalizedTargetSpaceId = String(targetSpaceId);
    const targetSpace = spaces.find((space) => String(space?.id) === normalizedTargetSpaceId);
    if (!targetSpace) return;
    const targetSection = spaceSectionRefs.current[targetSpaceId];
    if (!targetSection) return;

    targetSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [targetSpaceId, loading, displayProjects, spaces]);

  const handleStatusFilterChange = (event) => {
    const nextStatus = event.target.value;
    expandAllSpaces();
    setSelectedStatus(nextStatus);
    sessionStorage.setItem(STORAGE_KEYS.PROJECT_STATUS_FILTER, nextStatus);
    const nextParams = new URLSearchParams(searchParams);
    if (nextStatus) nextParams.set('status', nextStatus);
    else nextParams.delete('status');
    setSearchParams(nextParams, { replace: true });
  };

  const toggleSpace = (spaceKey) => {
    const normalizedSpaceKey = String(spaceKey);
    setCollapsedSpaces((currentSpaces) => {
      const nextSpaces = currentSpaces.includes(normalizedSpaceKey)
        ? currentSpaces.filter((key) => key !== normalizedSpaceKey)
        : [...currentSpaces, normalizedSpaceKey];
      sessionStorage.setItem(STORAGE_KEYS.PROJECT_COLLAPSED_SPACES, JSON.stringify(nextSpaces));
      return nextSpaces;
    });
  };
  
  // Reusable prefix validation function
  const validatePrefix = (prefix, currentPrefix = null) => {
    if (prefix.length < PREFIX_MIN_LENGTH && prefix.length > 0) {
      return VALIDATION_MESSAGES.PREFIX_MIN_LENGTH;
    } else if (prefix.length >= PREFIX_MIN_LENGTH) {
      const allPrefixes = Object.values(allExistingPrefixes).flat();
      if (allPrefixes.includes(prefix) && prefix !== currentPrefix) {
        return VALIDATION_MESSAGES.PREFIX_ALREADY_EXISTS;
      }
    }
    return '';
  };
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '', space: '', lead: '', dueDate: '', status: 'active', ticket_number_prefix: '', projectType: PROJECT_TYPES.KANBAN, productOwner: [], scrumMaster: '' });
  const [editFormData, setEditFormData] = useState({ name: '', description: '', space: '', lead: '', dueDate: '', status: '', ticket_number_prefix: '', projectType: PROJECT_TYPES.KANBAN, productOwner: [], scrumMaster: '' });
  const [originalEditFormData, setOriginalEditFormData] = useState(null);
  
  const [createStep, setCreateStep] = useState(1);
  const [editStep, setEditStep] = useState(1);
  const [scrumRoles, setScrumRoles] = useState({});
  const [editScrumRoles, setEditScrumRoles] = useState({});
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [editSelectedMembers, setEditSelectedMembers] = useState([]);
  const [originalEditSelectedMembers, setOriginalEditSelectedMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editSearchTerm, setEditSearchTerm] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [prefixError, setPrefixError] = useState('');
  const [prefixLookupError, setPrefixLookupError] = useState('');
  const [editPrefixError, setEditPrefixError] = useState('');
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [isUpdatingProject, setIsUpdatingProject] = useState(false);

  const normalizeMemberIds = (memberIds = []) => (
    Array.from(new Set(memberIds.filter(Boolean))).sort()
  );

  const isEditProjectUnchanged = React.useMemo(() => {
    if (!originalEditFormData) return true;
    const normalizeProjectForm = (data) => ({
      name: data.name || '',
      description: data.description || '',
      space: data.space || '',
      lead: data.lead || '',
      dueDate: data.dueDate || '',
      status: data.status || '',
      ticket_number_prefix: data.ticket_number_prefix || '',
      projectType: data.projectType || PROJECT_TYPES.KANBAN,
      productOwner: data.productOwner || [],
      scrumMaster: data.scrumMaster || ''
    });

    return (
      JSON.stringify(normalizeProjectForm(editFormData)) === JSON.stringify(normalizeProjectForm(originalEditFormData)) &&
      JSON.stringify(normalizeMemberIds(editSelectedMembers)) === JSON.stringify(normalizeMemberIds(originalEditSelectedMembers))
    );
  }, [editFormData, editSelectedMembers, originalEditFormData, originalEditSelectedMembers]);
  
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      dispatch(fetchDropdownData());
      dispatch(fetchProjects());
      dispatch(fetchProjectDropdownsByUser());

      try {
        await dispatch(fetchAllExistingPrefixes()).unwrap();
        if (isMounted) {
          setPrefixLookupError('');
        }
      } catch (errorMessage) {
        if (isMounted) {
          const errorText = typeof errorMessage === 'string' ? errorMessage : (errorMessage?.message || t('projects.prefixLoadFailed', 'You do not have access to this project'));
          setPrefixLookupError(errorText);
        }
        const toastErrorText = typeof errorMessage === 'string' ? errorMessage : (errorMessage?.message || t('projects.prefixLoadFailed', 'You do not have access to this project'));
        toast.error(toastErrorText);
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [dispatch, t]);



  const openCreateProject = () => {
    setFormData({ name: '', description: '', space: '', lead: '', dueDate: '', status: 'active', ticket_number_prefix: '', projectType: PROJECT_TYPES.KANBAN, productOwner: [], scrumMaster: '' });
    setShowCreateModal(true);
  };
  
  const closeCreateProject = (options = {}) => {
    const force = options?.force === true;
    if (isCreatingProject && !force) return;
    setCreateStep(1);
    setScrumRoles({});
    setShowCreateModal(false);
    setFormData({ name: '', description: '', space: '', lead: '', dueDate: '', status: 'active', ticket_number_prefix: '', projectType: PROJECT_TYPES.KANBAN, productOwner: [], scrumMaster: '' });
    setSelectedMembers([]);
    setSearchTerm('');
    setPrefixError('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value;
    
    if (name === 'ticket_number_prefix') {
      // Only allow alphanumeric chars, remove special chars and spaces, uppercase, max chars
      updatedValue = value.replace(REGEX.nonAlphanumeric, '').toUpperCase().slice(0, PREFIX_MAX_LENGTH);
    }
    
    // Update form data first
    const newFormData = { ...formData, [name]: updatedValue };
    setFormData(newFormData);
    
    // Then validate prefix with updated data
    if (name === 'ticket_number_prefix') {
      setPrefixError(validatePrefix(updatedValue));
    }
    
    if (name === PROJECT.SPACE) {
      if (value) {
        dispatch(fetchAllUsers(value));
      }
      // Reset lead and members when space changes
      setFormData(prev => ({ ...prev, lead: '', [name]: updatedValue }));
      setSelectedMembers([]);
      return;
    }
    if (name === PROJECT.LEAD && value) {
      setSelectedMembers(prev => (
        prev.includes(value) ? prev : [...prev, value]
      ));
    }
    if ((name === 'scrumMaster' || name === 'productOwner') && value) {
      const valuesToAdd = Array.isArray(value) ? value : [value];
      setSelectedMembers(prev => {
        const newMembers = valuesToAdd.filter(v => !prev.includes(v));
        return newMembers.length > 0 ? [...prev, ...newMembers] : prev;
      });
    }
  };

  const handleMemberToggle = (userId) => {
    if (formData.projectType === PROJECT_TYPES.SCRUM) {
      if (formData.scrumMaster === userId || (formData.productOwner || []).includes(userId)) {
        return;
      }
    }

    setSelectedMembers(prev => {
      const isRemoving = prev.includes(userId);
      if (isRemoving) {
        // If removing the project lead from team members, clear the lead field
        if (formData.lead === userId) {
          setFormData(prevForm => ({ ...prevForm, lead: '' }));
        }
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleCreateProject = async () => {
    if (isCreatingProject) return;
    
    if (formData.projectType === PROJECT_TYPES.SCRUM && createStep === 1) {
      const allSelectedUsers = Array.from(new Set([formData.scrumMaster, ...formData.productOwner, ...selectedMembers].filter(Boolean)));
      const initialRoles = {};
      allSelectedUsers.forEach(userId => {
        if (userId === formData.scrumMaster) {
          initialRoles[userId] = SCRUM_ROLES.SCRUM_MASTER;
        } else if (formData.productOwner.includes(userId)) {
          initialRoles[userId] = SCRUM_ROLES.PRODUCT_OWNER;
        } else {
          initialRoles[userId] = SCRUM_ROLES.SCRUM_TEAM;
        }
      });
      setScrumRoles(initialRoles);
      setCreateStep(2);
      return;
    }
    
    let finalScrumMaster = formData.scrumMaster;
    let finalProductOwner = [...formData.productOwner];
    let finalMembers = [...selectedMembers];
    
    if (formData.projectType === PROJECT_TYPES.SCRUM && createStep === 2) {
      const mastersCount = Object.values(scrumRoles).filter(role => role === SCRUM_ROLES.SCRUM_MASTER).length;
      const ownersCount = Object.values(scrumRoles).filter(role => role === SCRUM_ROLES.PRODUCT_OWNER).length;

      if (mastersCount !== 1) {
        toast.error(t('projects.scrumMasterValidation'));
        return;
      }
      if (ownersCount < 1) {
        toast.error(t('projects.productOwnerValidation'));
        return;
      }

      finalScrumMaster = Object.keys(scrumRoles).find(id => scrumRoles[id] === SCRUM_ROLES.SCRUM_MASTER) || '';
      finalProductOwner = Object.keys(scrumRoles).filter(id => scrumRoles[id] === SCRUM_ROLES.PRODUCT_OWNER);
      finalMembers = Object.keys(scrumRoles).filter(id => scrumRoles[id] === SCRUM_ROLES.SCRUM_TEAM);
    }
    
    const projectData = {
      name: formData.name,
      space_id: formData.space,
      description: formData.description,
      due_date: formData.dueDate,
      status: formData.status.toUpperCase(),
      ticket_number_prefix: formData.ticket_number_prefix,
      project_type: formData.projectType?.toUpperCase(),
      members: finalMembers
    };

    if (formData.projectType === PROJECT_TYPES.SCRUM) {
      projectData.po = finalProductOwner;
      projectData.scrum_master = finalScrumMaster;
    } else {
      projectData.lead_id = formData.lead;
    }
    
    setIsCreatingProject(true);
    try {
      await dispatch(createProject(projectData)).unwrap();
      toast.success(TOAST_MESSAGES.PROJECT_CREATED);
      closeCreateProject({ force: true });
      dispatch(fetchProjects());
      dispatch(fetchProjectDropdownsByUser());
    } catch (error) {
      console.error('Failed to create project:', error);
      toast.error(TOAST_MESSAGES.PROJECT_CREATE_FAILED);
    } finally {
      setIsCreatingProject(false);
    }
  };

  const openEditProject = async (project) => {
    setSelectedProject(project);
    
    // Find space ID by name
    const spaceId = spaces.find(space => space.name === Object.keys(projects).find(spaceName => 
      projects[spaceName].some(p => p.id === project.id)
    ))?.id || '';
    
    // Fetch users for the selected space first
    let spaceUsers = [];
    if (spaceId) {
      spaceUsers = await dispatch(fetchAllUsers(spaceId)).unwrap();
    }
    
    let result = null;
    let leadId = '';
    let memberIds = [];

    try {
      result = await dispatch(fetchProjectMembers({ projectId: project.id, status: PROJECT_DROPDOWN_STATUSES })).unwrap();
      
      if (result && typeof result === 'object' && Array.isArray(result.project_users)) {
        memberIds = result.project_users?.map(user => user.id);
        const leadUser = result.project_users?.find(user => user.name === project.lead_name);
        leadId = leadUser && spaceUsers.some(u => u.id === leadUser.id) ? leadUser.id : '';
        
        const activeUserIds = new Set(spaceUsers?.map(u => u?.id).filter(Boolean));
        const nextEditSelectedMembers = memberIds.filter(id => activeUserIds.has(id));
        setEditSelectedMembers(nextEditSelectedMembers);
      } else {
        setEditSelectedMembers([]);
      }
    } catch (error) {
      console.error('Error fetching project members:', error);
      setEditSelectedMembers([]);
    }
    
    // Set form data with the correct lead ID
    const nextEditFormData = {
      name: project?.name || '',
      description: project?.description || '',
      space: spaceId,
      lead: leadId,
      dueDate: project?.due_date ? project.due_date.split('T')[0] : '',
      status: project?.status?.toLowerCase() || '',
      ticket_number_prefix: project?.ticket_number_prefix || '',
      projectType: project?.project_type?.toUpperCase() === 'SCRUM' ? PROJECT_TYPES.SCRUM : PROJECT_TYPES.KANBAN,
      productOwner: project?.product_owner?.map(po => po.id) || [],
      scrumMaster: project?.scrum_master?.id || ''
    };

    setEditFormData(nextEditFormData);
    setOriginalEditFormData(nextEditFormData);
    setOriginalEditSelectedMembers(memberIds);
    
    setShowEditModal(true);
  };
  
  const closeEditProject = (options = {}) => {
    const force = options?.force === true;
    if (isUpdatingProject && !force) return;
    setEditStep(1);
    setEditScrumRoles({});
    setShowEditModal(false);
    setSelectedProject(null);
    setEditFormData({ name: '', description: '', space: '', lead: '', dueDate: '', status: '', ticket_number_prefix: '', projectType: PROJECT_TYPES.KANBAN, productOwner: [], scrumMaster: '' });
    setOriginalEditFormData(null);
    setEditSelectedMembers([]);
    setOriginalEditSelectedMembers([]);
    setEditSearchTerm('');
    setEditPrefixError('');
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value;
    
    if (name === 'ticket_number_prefix') {
      // Only allow alphanumeric chars, remove special chars and spaces, uppercase, max chars
      updatedValue = value.replace(REGEX.nonAlphanumeric, '').toUpperCase().slice(0, PREFIX_MAX_LENGTH);
    }
    
    // Update form data first
    const newEditFormData = { ...editFormData, [name]: updatedValue };
    setEditFormData(newEditFormData);
    
    // Then validate prefix with updated data
    if (name === 'ticket_number_prefix') {
      setEditPrefixError(validatePrefix(updatedValue, selectedProject?.ticket_number_prefix));
    }
    
    if (name === 'space') {
      if (value) {
        dispatch(fetchAllUsers(value));
      }
      // Reset lead and members when space changes
      setEditFormData(prev => ({ ...prev, lead: '', [name]: updatedValue }));
      setEditSelectedMembers([]);
      return;
    }
    if (name === 'lead' && value) {
      setEditSelectedMembers(prev => (
        prev.includes(value) ? prev : [...prev, value]
      ));
    }
    if ((name === 'scrumMaster' || name === 'productOwner') && value) {
      const valuesToAdd = Array.isArray(value) ? value : [value];
      setEditSelectedMembers(prev => {
        const newMembers = valuesToAdd.filter(v => !prev.includes(v));
        return newMembers.length > 0 ? [...prev, ...newMembers] : prev;
      });
    }
  };

  const handleEditMemberToggle = (userId) => {
    if (editFormData.projectType === PROJECT_TYPES.SCRUM) {
      if (editFormData.scrumMaster === userId || (editFormData.productOwner || []).includes(userId)) {
        return;
      }
    }

    setEditSelectedMembers(prev => {
      const isRemoving = prev.includes(userId);
      if (isRemoving && editFormData.lead === userId) {
        setEditFormData(prevForm => ({ ...prevForm, lead: '' }));
      }
      return isRemoving ? prev.filter(id => id !== userId) : [...prev, userId];
    });
  };

  const handleUpdateProject = async () => {
    if (isUpdatingProject) return;

    if (editFormData.projectType === PROJECT_TYPES.SCRUM && editStep === 1) {
      const allSelectedUsers = Array.from(new Set([editFormData.scrumMaster, ...editFormData.productOwner, ...editSelectedMembers].filter(Boolean)));
      const initialRoles = {};
      allSelectedUsers.forEach(userId => {
        if (userId === editFormData.scrumMaster) {
          initialRoles[userId] = SCRUM_ROLES.SCRUM_MASTER;
        } else if (editFormData.productOwner.includes(userId)) {
          initialRoles[userId] = SCRUM_ROLES.PRODUCT_OWNER;
        } else {
          initialRoles[userId] = SCRUM_ROLES.SCRUM_TEAM;
        }
      });
      setEditScrumRoles(initialRoles);
      setEditStep(2);
      return;
    }

    if (isEditProjectUnchanged && editStep === 1) return;

    

    let finalScrumMaster = editFormData.scrumMaster;
    let finalProductOwner = [...editFormData.productOwner];
    let finalMembers = [...editSelectedMembers];
    
    if (editFormData.projectType === PROJECT_TYPES.SCRUM && editStep === 2) {
      const mastersCount = Object.values(editScrumRoles).filter(role => role === SCRUM_ROLES.SCRUM_MASTER).length;
      const ownersCount = Object.values(editScrumRoles).filter(role => role === SCRUM_ROLES.PRODUCT_OWNER).length;

      if (mastersCount !== 1) {
        toast.error(t('projects.scrumMasterValidation'));
        return;
      }
      if (ownersCount < 1) {
        toast.error(t('projects.productOwnerValidation'));
        return;
      }

      finalScrumMaster = Object.keys(editScrumRoles).find(id => editScrumRoles[id] === SCRUM_ROLES.SCRUM_MASTER) || '';
      finalProductOwner = Object.keys(editScrumRoles).filter(id => editScrumRoles[id] === SCRUM_ROLES.PRODUCT_OWNER);
      finalMembers = Object.keys(editScrumRoles).filter(id => editScrumRoles[id] === SCRUM_ROLES.SCRUM_TEAM);
    }
    
    const projectData = {
      name: editFormData.name,
      space_id: editFormData.space,
      description: editFormData.description,
      due_date: editFormData.dueDate,
      status: editFormData.status.toUpperCase(),
      ticket_number_prefix: editFormData.ticket_number_prefix,
      members: finalMembers
    };

    if (editFormData.projectType === PROJECT_TYPES.SCRUM) {
      projectData.po = finalProductOwner;
      projectData.scrum_master = finalScrumMaster;
    } else {
      projectData.lead_id = editFormData.lead;
    }
    
    setIsUpdatingProject(true);
    try {
      await dispatch(updateProject({
        id: selectedProject.id,
        data: projectData,
        previousPrefix: selectedProject?.ticket_number_prefix
      })).unwrap();
      toast.success(TOAST_MESSAGES.PROJECT_UPDATED);
      closeEditProject({ force: true });
      dispatch(fetchProjects());
      
      // Refresh project dropdowns to sync access rights
      dispatch(fetchProjectDropdownsByUser());
    } catch (error) {
      console.error('Failed to update project:', error);
      toast.error(TOAST_MESSAGES.PROJECT_UPDATE_FAILED);
    } finally {
      setIsUpdatingProject(false);
    }
  };

  const handleDeleteProject = (project) => {
    setSelectedProject(project);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedProject(null);
  };

  const confirmDeleteProject = async () => {
    try {
      await dispatch(deleteProject({
        id: selectedProject.id,
        prefix: selectedProject?.ticket_number_prefix
      })).unwrap();
      toast.success(TOAST_MESSAGES.PROJECT_DELETED);
      closeDeleteModal();
      dispatch(fetchProjects());
      dispatch(fetchProjectDropdownsByUser());
    } catch (error) {
      console.error('Failed to delete project:', error);
      toast.error(TOAST_MESSAGES.PROJECT_DELETE_FAILED);
    }
  };

  const viewMembers = async (project) => {
    try {
      setSelectedProject(project);
      await dispatch(fetchProjectMembers({ projectId: project.id, status: PROJECT_DROPDOWN_STATUSES })).unwrap();
      setShowMembersModal(true);
    } catch (error) {
      console.error('Failed to load members:', error);
      toast.error('Failed to load project members');
    }
  };

  const closeMembersModal = () => {
    setShowMembersModal(false);
    setSelectedProject(null);
    dispatch(clearProjectMembers());
  };




  const renderProjectTable = ({ projects, spaceTitle, projectCount, spaceId, isCollapsed = false, onToggle, isLoadingPlaceholder = false, hideSectionHeader = false, emptyMessage, key = undefined }) => {
    const tableColumns = [
      { id: 'project', label: t("projects.project"), width: isSmallScreen ? '16%' : '20%', maxWidth: '260px' },
      { id: 'projectCode', label: t("projects.projectCode"), width: isSmallScreen ? '10%' : '11%' },
      { id: 'projectType', label: t("projects.projectType"), width: isSmallScreen ? '10%' : '10%' },
      { id: 'lead', label: t("projects.lead"), width: isSmallScreen ? '14%' : '15%', maxWidth: '250px' },
      { id: 'teamSize', label: t("projects.teamSize"), width: isSmallScreen ? '10%' : '11%' },
      { id: 'status', label: t("common.status"), width: isSmallScreen ? '10%' : '9%' },
      { id: 'dueDate', label: t("projects.dueDate"), width: isSmallScreen ? '12%' : '12%' },
      { id: 'actions', label: t("common.actions"), width: isSmallScreen ? '18%' : '12%' }
    ];

    const renderProjectRow = (project) => {
      const isAvailableInDropdown = projectDropdownsByUser?.some(p => p?.id === project?.id) || false;
      return (
        <TableRow key={project.id}>
          <TableCell>
            <OverflowTooltip
              title={project.name || ''}
              tooltipWidth={400}
              variant="body1"
              placement="top-start"
              sx={projectManagementStyles.projectName(isAvailableInDropdown)}
              onClick={() => {
                if (isAvailableInDropdown) {
                  dispatch(clearFilters());
                  dispatch(setFilters({ selectedProject: project?.id }));
                  dispatch(saveUserPreference({
                    preferenceKey: 'last_selected_project',
                    preferenceValue: { project_id: project?.id }
                  }));
                  if (project?.project_type?.toUpperCase() === 'SCRUM') {
                    navigate('/backlog-management');
                  } else {
                    navigate('/ticket-management');
                  }
                }
              }}
            />
          </TableCell>
          <TableCell>
            <OverflowTooltip
              title={project?.ticket_number_prefix || 'N/A'}
              tooltipWidth={240}
              variant="body2"
              placement="top-start"
            />
          </TableCell>
          <TableCell>
            <OverflowTooltip
              title={project?.project_type?.toUpperCase() === 'SCRUM' ? PROJECT_TYPES.SCRUM : PROJECT_TYPES.KANBAN}
              tooltipWidth={240}
              variant="body2"
              placement="top-start"
            />
          </TableCell>
          <TableCell sx={{ maxWidth: '250px', overflow: 'hidden' }}>
            {project?.project_type?.toUpperCase() === 'SCRUM' && project?.product_owner?.length > 0 ? (
              <Box className="pm-owner-container">
                <OverflowTooltip
                  title={project.product_owner.map(o => o.name).join(', ')}
                  tooltipWidth={400}
                  variant="body2"
                  placement="top-start"
                  className="pm-owner-name-text"
                >
                  {project.product_owner[0].name}
                </OverflowTooltip>
                {project.product_owner.length > 1 && (
                  <Tooltip
                    title={project.product_owner.map(o => o.name).join(', ')}
                    placement="top"
                    arrow
                    classes={{ tooltip: 'app-tooltip' }}
                  >
                    <Box className="pm-extra-owners-chip">
                      +{project.product_owner.length - 1}
                    </Box>
                  </Tooltip>
                )}
              </Box>
            ) : (
              <OverflowTooltip
                title={project?.lead_name || t('common.notAvailable')}
                tooltipWidth={400}
                variant="body2"
                placement="top-start"
              />
            )}
          </TableCell>
          <TableCell>
            <OverflowTooltip
              title={`${project?.total_users || 0} ${t('projects.members')}`}
              tooltipWidth={240}
              variant="body2"
              placement="top-start"
            />
          </TableCell>
          <TableCell>
            <OverflowTooltip
              title={project?.status || 'Unknown'}
              tooltipWidth={240}
              placement="top"
              component={Chip}
              label={project?.status || 'Unknown'}
              size="small"
              sx={{
                display: 'inline-flex',
                maxWidth: 'none',
                width: 'auto',
                bgcolor: project?.status?.toLowerCase() === 'active' ? COLORS.successLight : 'rgba(239, 68, 68, 0.1)',
                color: project?.status?.toLowerCase() === 'active' ? COLORS.successDark : COLORS.error,
                border: `1px solid ${project?.status?.toLowerCase() === 'active' ? COLORS.successDark : COLORS.error}`,
                fontWeight: 600
              }}
            />
          </TableCell>
          <TableCell>
            <OverflowTooltip
              title={project?.due_date ? new Date(project.due_date).toLocaleDateString("en-GB") : t('projects.noDueDate')}
              tooltipWidth={240}
              variant="body2"
              placement="top-start"
            />
          </TableCell>
          <TableCell>
            <Box sx={projectManagementStyles.actionButtons}>
              <IconButton 
                size="small" 
                onClick={() => navigate(`/strategist-agent?projectId=${project.id}`)}
                title={t('agent.title')}
                disabled={!canManageProjects}
              >
                <AutoAwesome fontSize="small" sx={{ color: !canManageProjects ? COLORS.gray[400] : COLORS.brand.primaryDark }} />
              </IconButton>
              <IconButton size="small" onClick={() => viewMembers(project)}>
                <Group fontSize="small" sx={{ color: COLORS.brand.primaryDark }} />
              </IconButton>
              <IconButton size="small" onClick={() => openEditProject(project)} disabled={!canManageProjects}>
                <img src={ICONS.editBlue} alt="Edit" style={{ width: '18px', height: '18px', opacity: !canManageProjects ? 0.5 : 1 }} />
              </IconButton>
              <IconButton size="small" onClick={() => handleDeleteProject(project)} disabled={!canManageProjects}>
                <img src={ICONS.deleteRed} alt="Delete" style={{ width: '18px', height: '18px', opacity: !canManageProjects ? 0.5 : 1 }} />
              </IconButton>
            </Box>
          </TableCell>
        </TableRow>
      );
    };

    return (
      <Box
        key={key}
        ref={(element) => {
          if (!spaceId) return;
          if (element) spaceSectionRefs.current[String(spaceId)] = element;
          else delete spaceSectionRefs.current[String(spaceId)];
        }}
        sx={{
          mb: 3,
          border: `1px solid ${COLORS.ui.ghostBorder}`,
          borderRadius: '8px',
          p: 2,
          bgcolor: COLORS.white,
          overflow: 'hidden'
        }}
      >
        {!hideSectionHeader && <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: isCollapsed ? 0 : 2 }}>
          {isLoadingPlaceholder ? (
            <Skeleton variant="text" width={180} height={32} animation="wave" />
          ) : (
            <Typography variant="h6" fontWeight="700" sx={{ color: COLORS.brand.primaryDark, fontSize: '1.1rem' }}>
              {spaceTitle}
            </Typography>
          )}
          <Box display="flex" gap={2} alignItems="center">
            {isLoadingPlaceholder ? (
              <Skeleton variant="rounded" width={96} height={32} animation="wave" />
            ) : (
              <Chip 
                label={`${projectCount} Project`} 
                variant="outlined"
                sx={{ 
                  color: COLORS.brand.primaryDark, 
                  borderColor: COLORS.brand.primaryDark,
                  fontWeight: 600,
                  borderRadius: '16px'
                }}
              />
            )}
            {!isLoadingPlaceholder && onToggle && (
              <Tooltip title={isCollapsed ? t('projects.expandSpace', 'Expand space') : t('projects.collapseSpace', 'Collapse space')}>
                <IconButton
                  size="small"
                  onClick={onToggle}
                  aria-label={isCollapsed ? `Expand ${spaceTitle}` : `Collapse ${spaceTitle}`}
                  aria-expanded={!isCollapsed}
                  sx={{ color: COLORS.brand.primaryDark }}
                >
                  {isCollapsed ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>}

        {!isCollapsed && <CommonTable
          columns={tableColumns}
          data={projects}
          renderRow={renderProjectRow}
          loading={loading}
          emptyMessage={emptyMessage || t("projects.noProjectsMessage")}
          headerBg={COLORS.tableHeaderBg}
          tableContainerSx={{
            height: 'auto',
            maxHeight: '400px'
          }}
          cardSx={{
            border: '0 !important',
            borderColor: 'transparent !important',
            borderRadius: '8px',
            boxShadow: 'none',
            '& .MuiTableCell-root': {
              borderLeft: 'none',
              borderRight: 'none'
            },
            '& .MuiTableCell-root:last-of-type': {
              overflow: 'visible',
              whiteSpace: 'nowrap'
            },
            '& .MuiTableCell-root:last-of-type .MuiBox-root': {
              flexWrap: 'nowrap'
            }
          }}
          preserveRowsOnLoading
        />}
      </Box>
    );
  };

  return (
    <div className="dashboard">
      <Navbar />

      <main className="main">
        {/* Page Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ px: 2, py: 1, borderRadius: '8px' }}>
          <Typography variant="h6" fontWeight={600} color={COLORS.text.muted}>
            {t('projects.projectManagement')}
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <FormControl size="small" sx={{ minWidth: 140, bgcolor: COLORS.white }}>
              <InputLabel shrink>{t('common.status')}</InputLabel>
              <Select
                value={selectedStatus}
                label={t('common.status')}
                onChange={handleStatusFilterChange}
                displayEmpty
                renderValue={(value) => {
                  if (value === MANAGEMENT_FILTER_STATUS.ACTIVE) return t('common.active', 'Active');
                  if (value === MANAGEMENT_FILTER_STATUS.INACTIVE) return t('common.inactive', 'Inactive');
                  return t('common.all', 'All');
                }}
                inputProps={{ 'aria-label': t('common.status') }}
              >
                <MenuItem value="">{t('common.all', 'All')}</MenuItem>
                <MenuItem value={MANAGEMENT_FILTER_STATUS.ACTIVE}>{t('common.active', 'Active')}</MenuItem>
                <MenuItem value={MANAGEMENT_FILTER_STATUS.INACTIVE}>{t('common.inactive', 'Inactive')}</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              placeholder={t('projects.searchProjects')}
              value={projectSearchTerm}
              onChange={(event) => {
                expandAllSpaces();
                setProjectSearchTerm(event.target.value);
              }}
              className="user-search-input pm-action-search"
              sx={{ bgcolor: COLORS.white, '& .MuiOutlinedInput-root': { bgcolor: COLORS.white } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 20, color: COLORS.brand.primaryDark }} />
                  </InputAdornment>
                )
              }}
            />
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={openCreateProject}
              className="pm-action-button"
              disabled={!canManageProjects}
              sx={{ 
                bgcolor: COLORS.ui.buttonPaleBlue, 
                color: COLORS.brand.primaryDark, 
                boxShadow: 'none', 
                '&:hover': { bgcolor: COLORS.ui.buttonPaleBlueHover, boxShadow: 'none' }, 
                '&.Mui-disabled': { bgcolor: COLORS.gray[200], color: COLORS.gray[500] },
                textTransform: 'none', 
                fontWeight: 600, 
                borderRadius: '4px' 
              }}
            >
              {t('projects.addProject')}
            </Button>
          </Box>
        </Box>

        {loading && Object.keys(displayProjects).length === 0 ? (
          renderProjectTable({
            projects: [],
            spaceTitle: "",
            projectCount: 0,
            isLoadingPlaceholder: true
          })
        ) : hasProjectSearch && Object.keys(displayProjects).length === 0 ? (
          renderProjectTable({
            projects: [],
            projectCount: 0,
            hideSectionHeader: true,
            emptyMessage: t('projects.noProjectsFound')
          })
        ) : Object.keys(displayProjects).length === 0 ? (
          <Card elevation={0} sx={projectManagementStyles.emptyStateCard}>
            <CardContent>
              <Assignment sx={projectManagementStyles.emptyStateIcon} />
              <Typography variant="h5" fontWeight="600" color="text.secondary" sx={{ mb: 1 }}>
                {t('projects.noProjectsTitle')}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t('projects.noProjectsMessage')}
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={openCreateProject}
                disabled={!canManageProjects}
                sx={projectManagementStyles.createButton}
              >
                {t('projects.addProject')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          Object.entries(displayProjects)?.map(([spaceName, spaceProjects]) => {
            const spaceId = spaces.find(s => s.name === spaceName)?.id;
            const spaceKey = String(spaceId ?? spaceName);
            return renderProjectTable({
              key: spaceName,
              projects: spaceProjects,
              spaceTitle: spaceName,
              projectCount: spaceProjects.length,
              spaceId: spaceId,
              isCollapsed: collapsedSpaces.includes(spaceKey),
              onToggle: () => toggleSpace(spaceKey)
            });
          })
        )}
      </main>

      {/* Create Project Modal */}
      <CommonFormModal
        open={showCreateModal}
        onClose={createStep === 2 ? () => setCreateStep(1) : closeCreateProject}
        customWidth="700px"
        title={createStep === 1 ? t('projects.createNewProjectTitle') : ''}
        subtitle={createStep === 1 ? t('projects.createNewProject') : ''}
        onSubmit={handleCreateProject}
        submitLabel={(loading || isCreatingProject) ? <CircularProgress size={20} color="inherit" /> : (createStep === 1 && formData.projectType === PROJECT_TYPES.SCRUM ? t('projects.nextButton') : t('projects.createProjectButton'))}
        cancelLabel={createStep === 2 ? t('projects.backButton') : t("common.cancel")}
        isSubmitting={loading || isCreatingProject}
        isSubmitDisabled={createStep === 1 
          ? (!formData.name || !formData.space || (formData.projectType === PROJECT_TYPES.KANBAN && !formData.lead) || (formData.projectType === PROJECT_TYPES.SCRUM && (!formData.productOwner.length || !formData.scrumMaster)) || !formData.ticket_number_prefix || (formData.ticket_number_prefix?.length || 0) < PREFIX_MIN_LENGTH || !!prefixError || (formData.name?.length || 0) > CHARACTER_LIMITS.PROJECT_NAME)
          : false}
      >
        {createStep === 1 ? (
          <>
        <Box className="pm-form-field">
          <Typography className="pm-dialog-section-title">
            {t("projects.projectInformation")}
            <Box component="img" src={ICONS.userIcon} alt="" sx={{ width: 20, height: 20 }} />
          </Typography>
          <TextField
            fullWidth
            label={t("projects.projectName")}
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder={t("projects.enterProjectName")}
            required
            variant="outlined"
            inputProps={{ maxLength: CHARACTER_LIMITS.PROJECT_NAME }}
          />
          <Typography className="pm-character-count">
            {formData.name?.length || 0}/{CHARACTER_LIMITS.PROJECT_NAME} Characters
          </Typography>
        </Box>
        <Box className="pm-form-field">
          <Typography className="pm-dialog-section-title" sx={{ mb: 1 }}>
            {t("projects.projectTypeQuestion")}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box
              className={`pm-project-type-box ${formData.projectType === PROJECT_TYPES.KANBAN ? 'selected' : ''}`}
              onClick={() => handleInputChange({ target: { name: 'projectType', value: PROJECT_TYPES.KANBAN } })}
            >
              <Box className="pm-project-type-header">
                <img src={kanbanIcon} alt="Kanban" className="pm-project-type-icon" />
                <div className="pm-project-type-title">{t("projects.kanban")}</div>
              </Box>
              <div className="pm-project-type-desc">{t("projects.kanbanDesc")}</div>
            </Box>
            <Box
              className={`pm-project-type-box ${formData.projectType === PROJECT_TYPES.SCRUM ? 'selected' : ''}`}
              onClick={() => handleInputChange({ target: { name: 'projectType', value: PROJECT_TYPES.SCRUM } })}
            >
              <Box className="pm-project-type-header">
                <img src={scrumIcon} alt="Scrum" className="pm-project-type-icon" />
                <div className="pm-project-type-title">{t("projects.scrum")}</div>
              </Box>
              <div className="pm-project-type-desc">{t("projects.scrumDesc")}</div>
            </Box>
          </Box>
        </Box>
        <Box className="pm-form-field">
          <TextField
            fullWidth
            label={t("common.description")}
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder={t("projects.describeProject")}
            multiline
            minRows={2}
            maxRows={4}
            variant="outlined"
            inputProps={{ maxLength: CHARACTER_LIMITS.PROJECT_DESCRIPTION }}
          />
          <Typography className="pm-character-count">
            {formData.description?.length || 0}/{CHARACTER_LIMITS.PROJECT_DESCRIPTION} Characters
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(6, 1fr)' }, gap: '1.25rem', mb: '1.25rem' }}>
          <Box sx={{ gridColumn: { xs: 'span 1', lg: formData.projectType === PROJECT_TYPES.SCRUM ? 'span 2' : 'span 3' } }}>
            <TextField
              fullWidth
              label={t("projects.ticketNumberPrefix")}
              name="ticket_number_prefix"
              value={formData.ticket_number_prefix}
              onChange={handleInputChange}
              placeholder={t("projects.ticketPrefixPlaceholder")}
              variant="outlined"
              required
              inputProps={{ maxLength: PREFIX_MAX_LENGTH }}
              error={!!prefixError}
            />
            <Typography className="pm-prefix-helper-text">
              {prefixError || t("projects.ticketPrefixHelperText")}
            </Typography>
            {prefixLookupError && (
              <Typography className="pm-prefix-helper-text" sx={{ color: COLORS.error }}>
                {prefixLookupError}
              </Typography>
            )}
          </Box>
          <FormControl fullWidth variant="outlined" sx={{ gridColumn: { xs: 'span 1', lg: formData.projectType === PROJECT_TYPES.SCRUM ? 'span 2' : 'span 3' } }}>
            <InputLabel>{t("common.status")}</InputLabel>
            <Select 
              name="status" 
              value={formData.status} 
              onChange={handleInputChange} 
              label={t("common.status")}
              MenuProps={dropdownMenuProps}
            >
              {statusOptions?.map((status) => (
                 <MenuItem key={status} value={status.toLowerCase()} sx={dropdownMenuItemSx}>
                   {status}
                 </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth variant="outlined" sx={{ gridColumn: { xs: 'span 1', lg: formData.projectType === PROJECT_TYPES.SCRUM ? 'span 2' : 'span 3' } }}>
            <InputLabel>{t("projects.spaceRequired")}</InputLabel>
            <Select 
              name="space" 
              value={formData.space} 
              onChange={handleInputChange} 
              label={t("projects.spaceRequired")}
              disabled={dropdownsLoading}
              MenuProps={dropdownMenuProps}
              renderValue={(selected) => {
                const selectedSpace = spaces?.find((space) => space.id === selected);
                if (!selectedSpace) return '';
                return (
                  <Box sx={localStyles.selectValueWithAvatar}>
                    <Avatar sx={localStyles.userAvatar}>
                      {selectedSpace.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography component="span" variant="body2" sx={localStyles.selectValueText}>
                      {selectedSpace.name}
                    </Typography>
                  </Box>
                );
              }}
            >
              {spaces?.map((space) => (
                <MenuItem 
                  key={space.id} 
                  value={space.id} 
                  sx={dropdownMenuItemSx}
                >
                  <Avatar sx={localStyles.userAvatar}>
                    {space.name.charAt(0).toUpperCase()}
                  </Avatar>
                  <Typography component="span" variant="body2">{space.name}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {formData.projectType === PROJECT_TYPES.KANBAN ? (
            <FormControl fullWidth variant="outlined" sx={{ gridColumn: { xs: 'span 1', lg: 'span 3' } }}>
              <InputLabel>{t("projects.projectLeadRequired")}</InputLabel>
              <Select 
                name="lead" 
                value={formData.lead} 
                onChange={handleInputChange} 
                label={t("projects.projectLeadRequired")}
                disabled={!formData.space}
                MenuProps={dropdownMenuProps}
                renderValue={(selected) => {
                  const selectedUser = users?.find(u => u.id === selected);
                  if (!selectedUser) return '';
                  const initials = getUserInitials(selectedUser.name);
                  return (
                    <Box sx={localStyles.selectValueWithAvatar}>
                      <Avatar sx={localStyles.userAvatar}>{initials.toUpperCase()}</Avatar>
                      <Typography component="span" variant="body2" sx={localStyles.selectValueText}>{selectedUser.name}</Typography>
                    </Box>
                  );
                }}
              >
                {allUsersLoading ? (
                  <MenuItem disabled sx={{ justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={20} sx={{ mr: 1.5, color: COLORS.brand.primaryDark }} />
                    <Typography variant="body2" color="textSecondary">{t("projects.loadingUsers", "Loading users...")}</Typography>
                  </MenuItem>
                ) : formData.space ? (
                  users && users.length > 0 ? (
                    users.map((user) => {
                      const initials = getUserInitials(user?.name);
                      
                      return (
                        <MenuItem 
                          key={user?.id} 
                          value={user?.id} 
                          sx={dropdownMenuItemSx}
                        >
                          <Avatar sx={localStyles.userAvatar}>
                            {initials.toUpperCase()}
                          </Avatar>
                          <Typography variant="body2">{user?.name || t('common.unknownUser')}</Typography>
                        </MenuItem>
                      );
                    })
                  ) : (
                    <MenuItem disabled>{t("projects.noUsersFound")}</MenuItem>
                  )
                ) : []}
              </Select>
            </FormControl>
          ) : (
            <>
            <FormControl fullWidth variant="outlined" sx={{ gridColumn: { xs: 'span 1', lg: 'span 3' } }}>
              <InputLabel>{t("projects.scrumMasterRequired")}</InputLabel>
              <Select 
                name="scrumMaster" 
                value={formData.scrumMaster} 
                onChange={handleInputChange} 
                label={t("projects.scrumMasterRequired")}
                disabled={!formData.space}
                MenuProps={dropdownMenuProps}
                renderValue={(selected) => {
                  const selectedUser = users?.find(u => u.id === selected);
                  if (!selectedUser) return '';
                  const initials = getUserInitials(selectedUser.name);
                  return (
                    <Box sx={localStyles.selectValueWithAvatar}>
                      <Avatar sx={localStyles.userAvatar}>{initials.toUpperCase()}</Avatar>
                      <Typography component="span" variant="body2" sx={localStyles.selectValueText}>{selectedUser.name}</Typography>
                    </Box>
                  );
                }}
              >
                {allUsersLoading ? (
                  <MenuItem disabled sx={{ justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={20} sx={{ mr: 1.5, color: COLORS.brand.primaryDark }} />
                    <Typography variant="body2" color="textSecondary">{t("projects.loadingUsers", "Loading users...")}</Typography>
                  </MenuItem>
                ) : formData.space ? (
                  users && users.length > 0 ? (
                    users.map((user) => {
                      const initials = getUserInitials(user?.name);
                      
                      return (
                        <MenuItem 
                          key={user?.id} 
                          value={user?.id} 
                          disabled={formData.productOwner.includes(user?.id)}
                          sx={dropdownMenuItemSx}
                        >
                          <Avatar sx={localStyles.userAvatar}>
                            {initials.toUpperCase()}
                          </Avatar>
                          <Typography variant="body2">{user?.name || t('common.unknownUser')}</Typography>
                        </MenuItem>
                      );
                    })
                  ) : (
                    <MenuItem disabled>{t("projects.noUsersFound")}</MenuItem>
                  )
                ) : []}
              </Select>
            </FormControl>
            <FormControl fullWidth variant="outlined" sx={{ gridColumn: { xs: 'span 1', lg: 'span 3' } }}>
              <InputLabel>{t("projects.productOwnerRequired")}</InputLabel>
              <Select
                multiple
                name="productOwner"
                value={formData.productOwner}
                onChange={handleInputChange}
                label={t("projects.productOwnerRequired")}
                disabled={!formData.space}
                MenuProps={dropdownMenuProps}
                renderValue={(selected) => (
                  <Typography variant="body2" fontWeight="500">
                    {selected.length} {t("projects.members", "Members")} Selected
                  </Typography>
                )}
              >
                {allUsersLoading ? (
                  <MenuItem disabled sx={{ justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={20} sx={{ mr: 1.5, color: COLORS.brand.primaryDark }} />
                    <Typography variant="body2" color="textSecondary">{t("projects.loadingUsers", "Loading users...")}</Typography>
                  </MenuItem>
                ) : formData.space ? (
                  users && users.length > 0 ? (
                    users.map((user) => {
                      const initials = getUserInitials(user?.name);
                      
                      return (
                        <MenuItem 
                          key={user?.id} 
                          value={user?.id} 
                          disabled={user?.id === formData.scrumMaster}
                          sx={dropdownMenuItemSx}
                        >
                          <Checkbox checked={formData.productOwner.includes(user?.id)} size="small" sx={{ p: 0.5, '&.Mui-checked': { color: COLORS.brand.primaryDark } }} />
                          <Avatar sx={localStyles.userAvatar}>
                            {initials.toUpperCase()}
                          </Avatar>
                          <Typography variant="body2" sx={{ ml: 1 }}>{user?.name || t('common.unknownUser')}</Typography>
                        </MenuItem>
                      );
                    })
                  ) : (
                    <MenuItem disabled>{t("projects.noUsersFound")}</MenuItem>
                  )
                ) : []}
              </Select>
            </FormControl>
            </>
          )}

        </Box>

        <Box className="pm-form-grid">
          <TextField
            fullWidth
            label={t("projects.dueDate")}
            name="dueDate"
            type="date"
            value={formData.dueDate}
            onChange={handleInputChange}
            onKeyDown={(e) => e.preventDefault()}
            InputLabelProps={{ shrink: true }}
            variant="outlined"
          />
          <FormControl fullWidth variant="outlined">
            <InputLabel>{t("projects.activeTeamMembers", "Active Team Members")}</InputLabel>
            <Select
              multiple
              value={selectedMembers}
              label={t("projects.activeTeamMembers", "Active Team Members")}
              disabled={!formData.space}
              renderValue={(selected) => (
                <Typography variant="body2" fontWeight="500">
                  {selected.length} {t("projects.members")} Selected
                </Typography>
              )}
              onOpen={() => setSearchTerm('')}
              MenuProps={dropdownMenuProps}
            >
              <Box sx={{ p: 1.5, position: 'sticky', top: 0, bgcolor: 'white', zIndex: 1, borderBottom: `1px solid ${COLORS.ui.ghostBorder}` }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t("projects.searchUsers")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 20, opacity: 0.6 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              {allUsersLoading ? (
                <MenuItem disabled sx={{ justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={20} sx={{ mr: 1.5, color: COLORS.brand.primaryDark }} />
                  <Typography variant="body2" color="textSecondary">{t("projects.loadingUsers", "Loading users...")}</Typography>
                </MenuItem>
              ) : formData.space ? (
                (() => {
                  const filtered = users?.filter(user => 
                    user?.name?.toLowerCase().includes(searchTerm.toLowerCase())
                  ) || [];
                  if (filtered.length > 0) {
                    return filtered.map((user) => {
                      const initials = getUserInitials(user?.name);
                      const isCoreRole = formData.projectType === PROJECT_TYPES.SCRUM 
                        ? (formData.scrumMaster === user.id || (formData.productOwner || []).includes(user.id))
                        : (formData.lead === user.id);
                      return (
                        <MenuItem 
                          key={user.id} 
                          value={user.id} 
                          disabled={isCoreRole}
                          onClick={() => !isCoreRole && handleMemberToggle(user.id)}
                          sx={{...dropdownMenuItemSx, opacity: isCoreRole ? 0.6 : 1}}
                        >
                          <Checkbox checked={selectedMembers.includes(user.id)} disabled={isCoreRole} size="small" sx={{ p: 0.5, '&.Mui-checked': { color: isCoreRole ? COLORS.gray[400] : COLORS.brand.primaryDark } }} />
                          <Avatar sx={{ ...localStyles.userAvatar }}>
                            {initials.toUpperCase()}
                          </Avatar>
                          <ListItemText 
                            primary={<Typography variant="body2" fontWeight="500">{user.name}</Typography>} 
                            secondary={<Typography variant="caption" color="text.secondary">{cleanDeletedEmail(user.email)}</Typography>} 
                          />
                        </MenuItem>
                      );
                    });
                  }
                  return (
                    <MenuItem disabled>
                      {users?.length > 0 ? t("projects.noMatchingUsers") : t("projects.noUsersFound")}
                    </MenuItem>
                  );
                })()
              ) : (
                <MenuItem disabled>{t("projects.selectSpaceFirst")}</MenuItem>
              )}
            </Select>
          </FormControl>
        </Box>
          </>
        ) : (
          <Box className="pm-modal-step-transition">
            <Typography className="pm-role-mapping-title">
              {t('projects.roleAndMapping')}
            </Typography>
            <Box className="pm-role-mapping-separator" />
            <Box className="pm-role-mapping-list-container">
              {Object.keys(scrumRoles).map(userId => {
                const userObj = users?.find(u => u.id === userId);
                if (!userObj) return null;
                return (
                  <Box key={userId} className="pm-role-list-item">
                    <OverflowTooltip 
                      className="pm-role-user-name" 
                      variant="body2" 
                      fontWeight="500" 
                      title={userObj.name} 
                    />
                    <FormControl variant="outlined" size="small">
                      <Select
                        value={scrumRoles[userId]}
                        onChange={(e) => setScrumRoles({ ...scrumRoles, [userId]: e.target.value })}
                        className="pm-role-dropdown"
                        MenuProps={roleDropdownMenuProps}
                      >
                        <MenuItem value="Scrum Master" className="pm-role-dropdown-item">{t('projects.scrumMasterRole')}</MenuItem>
                        <MenuItem value="Product Owner" className="pm-role-dropdown-item">{t('projects.productOwnerRole')}</MenuItem>
                        <MenuItem value="Scrum Team" className="pm-role-dropdown-item">{t('projects.scrumTeamRole')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </CommonFormModal>


      {/* Edit Project Modal */}
      <CommonFormModal
        open={showEditModal}
        onClose={editStep === 2 ? () => setEditStep(1) : closeEditProject}
        customWidth="700px"
        title={editStep === 1 ? t('projects.editProjectTitle') : ''}
        subtitle={editStep === 1 ? t('projects.updateProjectDetails') : ''}
        onSubmit={handleUpdateProject}
        submitLabel={(loading || isUpdatingProject) ? <CircularProgress size={20} color="inherit" /> : (editStep === 1 && editFormData.projectType === PROJECT_TYPES.SCRUM ? t('projects.nextButton') : t('projects.updateProjectButton'))}
        cancelLabel={editStep === 2 ? t('projects.backButton') : t("common.cancel")}
        isSubmitting={loading || isUpdatingProject}
        isSubmitDisabled={editStep === 1 
          ? (isEditProjectUnchanged || !editFormData.name || !editFormData.space || (editFormData.projectType === PROJECT_TYPES.KANBAN && !editFormData.lead) || (editFormData.projectType === PROJECT_TYPES.SCRUM && (!editFormData.productOwner.length || !editFormData.scrumMaster)) || !editFormData.ticket_number_prefix || (editFormData.ticket_number_prefix?.length || 0) < PREFIX_MIN_LENGTH || !!editPrefixError || (editFormData.name?.length || 0) > CHARACTER_LIMITS.PROJECT_NAME)
          : false}
      >
        {editStep === 1 ? (
          <>
        <Box className="pm-form-field">
          <Typography className="pm-dialog-section-title">
            {t("projects.projectInformation")}
            <Box component="img" src={ICONS.userIcon} alt="" sx={{ width: 20, height: 20 }} />
          </Typography>
          <TextField
            fullWidth
            label={t("projects.projectName")}
            name="name"
            value={editFormData.name}
            onChange={handleEditInputChange}
            placeholder={t("projects.enterProjectName")}
            required
            variant="outlined"
            inputProps={{ maxLength: CHARACTER_LIMITS.PROJECT_NAME }}
          />
          <Typography className="pm-character-count">
            {editFormData.name?.length || 0}/{CHARACTER_LIMITS.PROJECT_NAME} Characters
          </Typography>
        </Box>
        <Box className="pm-form-field">
          <Typography className="pm-dialog-section-title" sx={{ mb: 1 }}>
            {t("projects.projectTypeQuestion")}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box
              className={`pm-project-type-box disabled ${editFormData.projectType === PROJECT_TYPES.KANBAN ? 'selected' : ''}`}
            >
              <Box className="pm-project-type-header">
                <img src={kanbanIcon} alt="Kanban" className="pm-project-type-icon" />
                <div className="pm-project-type-title">{t("projects.kanban")}</div>
              </Box>
              <div className="pm-project-type-desc">{t("projects.kanbanDesc")}</div>
            </Box>
            <Box
              className={`pm-project-type-box disabled ${editFormData.projectType === PROJECT_TYPES.SCRUM ? 'selected' : ''}`}
            >
              <Box className="pm-project-type-header">
                <img src={scrumIcon} alt="Scrum" className="pm-project-type-icon" />
                <div className="pm-project-type-title">{t("projects.scrum")}</div>
              </Box>
              <div className="pm-project-type-desc">{t("projects.scrumDesc")}</div>
            </Box>
          </Box>
        </Box>
        <Box className="pm-form-field">
          <TextField
            fullWidth
            label={t("common.description")}
            name="description"
            value={editFormData.description}
            onChange={handleEditInputChange}
            placeholder={t("projects.describeProject")}
            multiline
            minRows={2}
            maxRows={4}
            variant="outlined"
            inputProps={{ maxLength: CHARACTER_LIMITS.PROJECT_DESCRIPTION }}
          />
          <Typography className="pm-character-count">
            {editFormData.description?.length || 0}/{CHARACTER_LIMITS.PROJECT_DESCRIPTION} Characters
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(6, 1fr)' }, gap: '1.25rem', mb: '1.25rem' }}>
          <Box sx={{ gridColumn: { xs: 'span 1', lg: editFormData.projectType === PROJECT_TYPES.SCRUM ? 'span 2' : 'span 3' } }}>
            <TextField
              fullWidth
              label={t("projects.ticketNumberPrefix")}
              name="ticket_number_prefix"
              value={editFormData.ticket_number_prefix}
              onChange={handleEditInputChange}
              placeholder={t("projects.ticketPrefixPlaceholder")}
              variant="outlined"
              required
              inputProps={{ maxLength: PREFIX_MAX_LENGTH }}
              error={!!editPrefixError}
            />
            <Typography className="pm-prefix-helper-text">
              {editPrefixError || t("projects.ticketPrefixHelperText")}
            </Typography>
            {prefixLookupError && (
              <Typography className="pm-prefix-helper-text" sx={{ color: COLORS.error }}>
                {prefixLookupError}
              </Typography>
            )}
          </Box>
          <FormControl fullWidth variant="outlined" sx={{ gridColumn: { xs: 'span 1', lg: editFormData.projectType === PROJECT_TYPES.SCRUM ? 'span 2' : 'span 3' } }}>
            <InputLabel>{t("common.status")}</InputLabel>
            <Select 
              name="status" 
              value={editFormData.status} 
              onChange={handleEditInputChange} 
              label={t("common.status")}
              MenuProps={dropdownMenuProps}
            >
              {statusOptions?.map((status) => (
                <MenuItem key={status} value={status.toLowerCase()} sx={dropdownMenuItemSx}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth variant="outlined" sx={{ gridColumn: { xs: 'span 1', lg: editFormData.projectType === PROJECT_TYPES.SCRUM ? 'span 2' : 'span 3' } }}>
            <InputLabel>{t("projects.spaceRequired")}</InputLabel>
            <Select 
              name="space" 
              value={editFormData.space} 
              onChange={handleEditInputChange} 
              label={t("projects.spaceRequired")}
              disabled={dropdownsLoading}
              MenuProps={dropdownMenuProps}
              renderValue={(selected) => {
                const selectedSpace = spaces?.find((space) => space.id === selected);
                if (!selectedSpace) return '';
                return (
                  <Box sx={localStyles.selectValueWithAvatar}>
                    <Avatar sx={localStyles.userAvatar}>
                      {selectedSpace.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography component="span" variant="body2" sx={localStyles.selectValueText}>
                      {selectedSpace.name}
                    </Typography>
                  </Box>
                );
              }}
            >
              {spaces?.map((space) => (
                <MenuItem 
                  key={space.id} 
                  value={space.id} 
                  sx={dropdownMenuItemSx}
                >
                  <Avatar sx={localStyles.userAvatar}>
                    {space.name.charAt(0).toUpperCase()}
                  </Avatar>
                  <Typography component="span" variant="body2">{space.name}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {editFormData.projectType === PROJECT_TYPES.KANBAN ? (
            <FormControl fullWidth variant="outlined" sx={{ gridColumn: { xs: 'span 1', lg: 'span 3' } }}>
              <InputLabel>{t("projects.projectLeadRequired")}</InputLabel>
              <Select 
                name="lead" 
                value={editFormData.lead} 
                onChange={handleEditInputChange} 
                label={t("projects.projectLeadRequired")}
                disabled={!editFormData.space}
                MenuProps={dropdownMenuProps}
                renderValue={(selected) => {
                  const selectedUser = users?.find(u => u.id === selected);
                  if (!selectedUser) return '';
                  const initials = getUserInitials(selectedUser.name);
                  return (
                    <Box sx={localStyles.selectValueWithAvatar}>
                      <Avatar sx={localStyles.userAvatar}>{initials.toUpperCase()}</Avatar>
                      <Typography component="span" variant="body2" sx={localStyles.selectValueText}>{selectedUser.name}</Typography>
                    </Box>
                  );
                }}
              >
                {allUsersLoading ? (
                  <MenuItem disabled sx={{ justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={20} sx={{ mr: 1.5, color: COLORS.brand.primaryDark }} />
                    <Typography variant="body2" color="textSecondary">{t("projects.loadingUsers", "Loading users...")}</Typography>
                  </MenuItem>
                ) : editFormData.space ? (
                  users && users.length > 0 ? (
                    users.map((user) => {
                      const initials = getUserInitials(user?.name);
                      
                      return (
                        <MenuItem 
                          key={user?.id} 
                          value={user?.id} 
                          sx={dropdownMenuItemSx}
                        >
                          <Avatar sx={localStyles.userAvatar}>
                            {initials.toUpperCase()}
                          </Avatar>
                          <Typography variant="body2">{user?.name || t('common.unknownUser')}</Typography>
                        </MenuItem>
                      );
                    })
                  ) : (
                    <MenuItem disabled>{t("projects.noUsersFound")}</MenuItem>
                  )
                ) : []}
              </Select>
            </FormControl>
          ) : (
            <>
            <FormControl fullWidth variant="outlined" sx={{ gridColumn: { xs: 'span 1', lg: 'span 3' } }}>
              <InputLabel>{t("projects.scrumMasterRequired")}</InputLabel>
              <Select 
                name="scrumMaster" 
                value={editFormData.scrumMaster} 
                onChange={handleEditInputChange} 
                label={t("projects.scrumMasterRequired")}
                disabled={!editFormData.space}
                MenuProps={dropdownMenuProps}
                renderValue={(selected) => {
                  const selectedUser = users?.find(u => u.id === selected);
                  if (!selectedUser) return '';
                  const initials = getUserInitials(selectedUser.name);
                  return (
                    <Box sx={localStyles.selectValueWithAvatar}>
                      <Avatar sx={localStyles.userAvatar}>{initials.toUpperCase()}</Avatar>
                      <Typography component="span" variant="body2" sx={localStyles.selectValueText}>{selectedUser.name}</Typography>
                    </Box>
                  );
                }}
              >
                {allUsersLoading ? (
                  <MenuItem disabled sx={{ justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={20} sx={{ mr: 1.5, color: COLORS.brand.primaryDark }} />
                    <Typography variant="body2" color="textSecondary">{t("projects.loadingUsers", "Loading users...")}</Typography>
                  </MenuItem>
                ) : editFormData.space ? (
                  users && users.length > 0 ? (
                    users.map((user) => {
                      const initials = getUserInitials(user?.name);
                      
                      return (
                        <MenuItem 
                          key={user?.id} 
                          value={user?.id} 
                          disabled={editFormData.productOwner.includes(user?.id)}
                          sx={dropdownMenuItemSx}
                        >
                          <Avatar sx={localStyles.userAvatar}>
                            {initials.toUpperCase()}
                          </Avatar>
                          <Typography variant="body2">{user?.name || t('common.unknownUser')}</Typography>
                        </MenuItem>
                      );
                    })
                  ) : (
                    <MenuItem disabled>{t("projects.noUsersFound")}</MenuItem>
                  )
                ) : []}
              </Select>
            </FormControl>
            <FormControl fullWidth variant="outlined" sx={{ gridColumn: { xs: 'span 1', lg: 'span 3' } }}>
              <InputLabel>{t("projects.productOwnerRequired")}</InputLabel>
              <Select
                multiple
                name="productOwner"
                value={editFormData.productOwner}
                onChange={handleEditInputChange}
                label={t("projects.productOwnerRequired")}
                disabled={!editFormData.space}
                MenuProps={dropdownMenuProps}
                renderValue={(selected) => (
                  <Typography variant="body2" fontWeight="500">
                    {selected.length} {t("projects.members", "Members")} Selected
                  </Typography>
                )}
              >
                {allUsersLoading ? (
                  <MenuItem disabled sx={{ justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={20} sx={{ mr: 1.5, color: COLORS.brand.primaryDark }} />
                    <Typography variant="body2" color="textSecondary">{t("projects.loadingUsers", "Loading users...")}</Typography>
                  </MenuItem>
                ) : editFormData.space ? (
                  users && users.length > 0 ? (
                    users.map((user) => {
                      const initials = getUserInitials(user?.name);
                      
                      return (
                        <MenuItem 
                          key={user?.id} 
                          value={user?.id} 
                          disabled={user?.id === editFormData.scrumMaster}
                          sx={dropdownMenuItemSx}
                        >
                          <Checkbox checked={editFormData.productOwner.includes(user?.id)} size="small" sx={{ p: 0.5, '&.Mui-checked': { color: COLORS.brand.primaryDark } }} />
                          <Avatar sx={localStyles.userAvatar}>
                            {initials.toUpperCase()}
                          </Avatar>
                          <Typography variant="body2" sx={{ ml: 1 }}>{user?.name || t('common.unknownUser')}</Typography>
                        </MenuItem>
                      );
                    })
                  ) : (
                    <MenuItem disabled>{t("projects.noUsersFound")}</MenuItem>
                  )
                ) : []}
              </Select>
            </FormControl>
            </>
          )}

        </Box>

        <Box className="pm-form-grid">
          <TextField
            fullWidth
            label={t("projects.dueDate")}
            name="dueDate"
            type="date"
            value={editFormData.dueDate}
            onChange={handleEditInputChange}
            onKeyDown={(e) => e.preventDefault()}
            InputLabelProps={{ shrink: true }}
            variant="outlined"
          />
          <FormControl fullWidth variant="outlined">
            <InputLabel>{t("projects.activeTeamMembers", "Active Team Members")}</InputLabel>
            <Select
              multiple
              value={editSelectedMembers}
              label={t("projects.activeTeamMembers", "Active Team Members")}
              disabled={!editFormData.space}
              renderValue={(selected) => (
                <Typography variant="body2" fontWeight="500">
                  {selected.length} {t("projects.members")} Selected
                </Typography>
              )}
              onOpen={() => setEditSearchTerm('')}
              MenuProps={dropdownMenuProps}
            >
              <Box sx={{ p: 1.5, position: 'sticky', top: 0, bgcolor: 'white', zIndex: 1, borderBottom: `1px solid ${COLORS.ui.ghostBorder}` }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t("projects.searchUsers")}
                  value={editSearchTerm}
                  onChange={(e) => setEditSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 20, opacity: 0.6 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              {allUsersLoading ? (
                <MenuItem disabled sx={{ justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={20} sx={{ mr: 1.5, color: COLORS.brand.primaryDark }} />
                  <Typography variant="body2" color="textSecondary">{t("projects.loadingUsers", "Loading users...")}</Typography>
                </MenuItem>
              ) : editFormData.space ? (
                (() => {
                  const filtered = users?.filter(user => 
                    user?.name?.toLowerCase().includes(editSearchTerm.toLowerCase())
                  ) || [];
                  if (filtered.length > 0) {
                    return filtered.map((user) => {
                      const initials = getUserInitials(user?.name);
                      const isCoreRole = editFormData.projectType === PROJECT_TYPES.SCRUM 
                        ? (editFormData.scrumMaster === user.id || (editFormData.productOwner || []).includes(user.id))
                        : (editFormData.lead === user.id);
                      return (
                        <MenuItem 
                          key={user.id} 
                          value={user.id} 
                          disabled={isCoreRole}
                          onClick={() => !isCoreRole && handleEditMemberToggle(user.id)}
                          sx={{...dropdownMenuItemSx, opacity: isCoreRole ? 0.6 : 1}}
                        >
                          <Checkbox checked={editSelectedMembers.includes(user.id)} disabled={isCoreRole} size="small" sx={{ p: 0.5, '&.Mui-checked': { color: isCoreRole ? COLORS.gray[400] : COLORS.brand.primaryDark } }} />
                          <Avatar sx={{ ...localStyles.userAvatar }}>
                            {initials.toUpperCase()}
                          </Avatar>
                          <ListItemText 
                            primary={<Typography variant="body2" fontWeight="500">{user.name}</Typography>} 
                            secondary={<Typography variant="caption" color="text.secondary">{cleanDeletedEmail(user.email)}</Typography>} 
                          />
                        </MenuItem>
                      );
                    });
                  }
                  return (
                    <MenuItem disabled>
                      {users?.length > 0 ? t("projects.noMatchingUsers") : t("projects.noUsersFound")}
                    </MenuItem>
                  );
                })()
              ) : (
                <MenuItem disabled>{t("projects.selectSpaceFirst")}</MenuItem>
              )}
            </Select>
          </FormControl>
        </Box>
          </>
        ) : (
          <Box className="pm-modal-step-transition">
            <Typography className="pm-role-mapping-title">
              {t('projects.roleAndMapping')}
            </Typography>
            <Box className="pm-role-mapping-separator" />
            <Box className="pm-role-mapping-list-container">
              {Object.keys(editScrumRoles).map(userId => {
                const userObj = users?.find(u => u.id === userId);
                if (!userObj) return null;
                return (
                  <Box key={userId} className="pm-role-list-item">
                    <OverflowTooltip 
                      className="pm-role-user-name" 
                      variant="body2" 
                      fontWeight="500" 
                      title={userObj.name} 
                    />
                    <FormControl variant="outlined" size="small">
                      <Select
                        value={editScrumRoles[userId]}
                        onChange={(e) => setEditScrumRoles({ ...editScrumRoles, [userId]: e.target.value })}
                        className="pm-role-dropdown"
                        MenuProps={roleDropdownMenuProps}
                      >
                        <MenuItem value="Scrum Master" className="pm-role-dropdown-item">{t('projects.scrumMasterRole')}</MenuItem>
                        <MenuItem value="Product Owner" className="pm-role-dropdown-item">{t('projects.productOwnerRole')}</MenuItem>
                        <MenuItem value="Scrum Team" className="pm-role-dropdown-item">{t('projects.scrumTeamRole')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </CommonFormModal>

      {/* {t("projects.deleteProjectButton")} Modal */}
      {/* Delete Project Modal */}
      <Dialog 
        open={showDeleteModal} 
        onClose={closeDeleteModal} 
        maxWidth="xs" 
        fullWidth
        PaperProps={{ className: 'pm-modal-paper' }}
      >
        <DialogTitle sx={{ pt: 4, pb: 1, position: 'relative' }}>
          <Box display="flex" flexDirection="column" alignItems="center" gap={1.5}>
            <Avatar sx={{ bgcolor: COLORS.ui.error8, color: COLORS.red.muiError, width: 56, height: 56 }}>
              <Warning sx={{ fontSize: '2rem' }} />
            </Avatar>
            <Typography variant="h6" fontWeight="700" color="text.primary" align="center" sx={{ mt: 1 }}>
              {t("projects.deleteProjectButton")}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2, textAlign: 'center' }}>
          <Typography sx={{ mb: 1.5, color: 'text.primary', fontSize: '0.95rem' }}>
            {t("projects.deleteConfirmation")}
          </Typography>
          <Box sx={{ bgcolor: COLORS.background, border: `1px solid ${COLORS.slate[200]}`, borderRadius: '6px', py: 1.5, px: 2, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="700" color={COLORS.red.muiError} sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
              {selectedProject?.name}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
            {t("projects.deleteWarning")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 4, pt: 1, display: 'flex', gap: 2 }}>
          <Button 
            onClick={closeDeleteModal} 
            variant="outlined" 
            disabled={loading} 
            sx={{ flex: 1, color: 'text.primary', borderColor: COLORS.gray[300], py: '0.5rem', textTransform: 'none', fontWeight: 600 }}
          >
            {t("common.cancel")}
          </Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={confirmDeleteProject} 
            disabled={loading} 
            sx={{ flex: 1, bgcolor: COLORS.red.muiError, color: COLORS.white, py: '0.5rem', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: COLORS.red.muiErrorHover } }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : t('projects.deleteProjectButton')}
          </Button>
        </DialogActions>
      </Dialog>

      <TeamMembersDialog
        open={showMembersModal}
        onClose={closeMembersModal}
        title={selectedProject?.name}
        subtitle={`Project ${t('projects.teamMembers')}`}
        members={projectMembers}
        leadName={selectedProject?.lead_name}
        projectType={selectedProject?.project_type?.toUpperCase()}
        scrumMasterId={selectedProject?.scrum_master?.id}
        productOwnerIds={selectedProject?.product_owner?.map(po => po.id) || []}
        labels={{
          totalMembers: t('projects.totalMembers'),
          teamMembers: t('projects.teamMembers'),
          unknownUser: t('common.unknownUser'),
          lead: t('projects.lead'),
          scrumMaster: t('projects.scrumMasterRole'),
          productOwner: t('projects.productOwnerRole'),
          active: t('common.active'),
          close: t('projects.close')
        }}
      />
    </div>
  );
};

export default ProjectManagement;
